import { useTheme } from '@emotion/react';
import { Image } from '../picture/picture';
import ViewSVG from '../../public/img/views2.svg';
import StarSVG from '../../public/img/star.svg';
import CheckMarkSVG from '../../public/img/check-mark.svg';
import { H1 } from '../styled/typography';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { Action } from '../../models';
import FavoriteButton from '../favorite/FavoriteButton';
import FavoriteButtonWrapper from '../favorite/FavoriteButtonWrapper';
import { FavoriteKinds } from '../../models/favorite';
import { RedButton } from '../styled/Button';
import { getBuyButtonText, isEventPassed } from '../../utils/event';
import wordDeclension from '../../utils/word-declension';
import { activeStatus } from '../../constants/events';
import { getBenefit, splitPriceByThree } from '../../utils';

export function EventBanner({
  item,
  disabled,
  onButtonClick,
  isRanOutOfTickets,
  tsNextWidgetConfig,
}: {
  item: Action;
  disabled?: boolean;
  isRanOutOfTickets: boolean;
  onButtonClick: () => void;
  tsNextWidgetConfig: null | {
    type: string;
    id: string;
  };
}) {
  const theme = useTheme();
  const getReview = (count: number) =>
    `${count} ${wordDeclension(count, 'отзыв', 'отзыва', 'отзывов')}`;

  return (
    <div
      css={{
        position: 'relative',
        width: '100%',
        height: 400,
        overflow: 'hidden',
        paddingBottom: 30,
        [DEVICE_SCREENS_MAX_STRING.md]: {
          marginBottom: 10,
        },
        [DEVICE_SCREENS_MAX_STRING.sm]: {
          marginBottom: -20,
        },
      }}
    >
      <Image
        css={{
          position: 'absolute',
          inset: 0,
          height: '100%',
          width: '100%',
          objectFit: 'cover',
          zIndex: 1,
        }}
        key={item.id}
        loading='eager'
        image={item.images[0]}
        alt={item.title}
      />
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'absolute',
          height: '100%',
          width: '100%',
          objectFit: 'cover',
          zIndex: 2,
          padding: '35px 20px 40px',
          [DEVICE_SCREENS_MAX_STRING.lg]: {
            padding: 20,
          },
          color: theme.colors.white,
          background:
            'linear-gradient(180deg, rgba(19, 19, 19, 0) 25.52%, #131313 100%)',
        }}
      >
        {disabled && (
          <div
            css={{
              width: 73,
              height: 40,
              background: 'rgba(125, 123, 122, 0.8)',
              borderRadius: '0px 4px 4px 0px',
              fontFamily: 'Montserrat',
              fontSize: 16,
              lineHeight: '22px',
              fontWeight: 500,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'absolute',
              left: 0,
            }}
          >
            Архив
          </div>
        )}
        <div
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {!!item.discount && !disabled && (
            <div
              css={{
                position: 'absolute',
                top: 35,
                left: 0,
                backgroundColor: 'rgba(244, 67, 54, 0.8)',
                boxShadow: '0 4px 10px 0 rgba(0, 0, 0, 0.1)',
                borderRadius: '0 4px 4px 0',
                fontFamily: 'Montserrat',
                fontWeight: 500,
                fontSize: 16,
                textAlign: 'center',
                padding: 10,
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  padding: 8,
                  fontSize: 14,
                  top: 20,
                },
              }}
            >
              −{item.discount}% скидка
            </div>
          )}
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              [DEVICE_SCREENS_MAX_STRING.sm]: { display: 'none' },
            }}
          >
            {!!item.views && (
              <div css={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <ViewSVG css={{ width: 20, height: 18 }} /> {item.views}
              </div>
            )}
            {!!item.age_category && <div>{item.age_category}</div>}
          </div>
        </div>
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            [DEVICE_SCREENS_MAX_STRING.xl]: {
              flexDirection: 'column',
              alignItems: 'unset',
              gap: 10,
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              marginBottom: 20,
            },
          }}
        >
          <div
            css={{
              display: 'flex',
              flexBasis: '50%',
              flexDirection: 'column',
              gap: 5,
            }}
          >
            {item.status === activeStatus && isRanOutOfTickets && (
              <div
                css={{
                  width: 170,
                  height: 40,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '5px 5px 5px 0',
                  fontFamily: 'Golos, serif',
                  fontSize: 14,
                  color: theme.colors.white,
                  backgroundColor: '#7D7B7A',
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    order: 1,
                    width: '100%',
                  },
                }}
              >
                <span>Билеты закончились</span>
              </div>
            )}
            <div css={{ display: 'flex', alignItems: 'flex-end' }}>
              {item.comments_avg_rating > 0 && (
                <>
                  <StarSVG css={{ width: 16, height: 16, color: '#FFC907' }} />
                  <span
                    css={{
                      fontFamily: 'Montserrat',
                      fontSize: 18,
                      lineHeight: '14px',
                      fontWeight: 'bold',
                      margin: '0 8px',
                    }}
                  >
                    {item.comments_avg_rating.toFixed(1)}
                  </span>
                </>
              )}
              {item.comments_count > 0 && (
                <button
                  onClick={() => {
                    const el = document.querySelector('.comments-block');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  type='button'
                  css={{
                    fontSize: 12,
                    textDecoration: 'underline dashed',
                    lineHeight: '14px',
                    ':hover': {
                      textDecoration: 'none',
                    },
                  }}
                >
                  {getReview(item.comments_count)}
                </button>
              )}
            </div>
            <H1
              css={{
                color: '#F8F8F8',
                fontFamily: 'Montserrat',
                letterSpacing: '0.01em',
                lineHeight: '38px',
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  fontSize: 32,
                },
                [DEVICE_SCREENS_MAX_STRING.sm]: {
                  fontSize: 22,
                  lineHeight: '26px',
                  padding: 0,
                  marginBottom: 10,
                },
              }}
            >
              {item.title}
            </H1>
          </div>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              [DEVICE_SCREENS_MAX_STRING.xl]: {
                marginLeft: 'auto',
              },
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                marginLeft: 0,
              },
            }}
          >
            {item.type !== 'FREE' &&
              item.display_price &&
              !isRanOutOfTickets && (
                <div
                  css={{
                    verticalAlign: 'middle',
                    fontFamily: 'Montserrat',
                    fontSize: 18,
                    lineHeight: '25px',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                >
                  {!!item.instead_price &&
                  item.instead_price > 0 &&
                  item.filter_price ? (
                    <span
                      title={
                        item.instead_price && item.filter_price
                          ? `Ваша экономия ${splitPriceByThree(
                              getBenefit(item.instead_price, item.filter_price)
                            )}₽`
                          : ''
                      }
                    >
                      {item.display_price.indexOf('от') !== -1 ? 'от ' : ''}
                      <span
                        css={{
                          textDecoration: 'red line-through',
                          marginRight: 5,
                        }}
                      >
                        {splitPriceByThree(item.instead_price)}
                      </span>
                      <span
                        css={{
                          fontSize: 32,
                          lineHeight: '32px',
                          color: 'red',
                        }}
                      >
                        {splitPriceByThree(item.filter_price)}₽
                      </span>
                    </span>
                  ) : (
                    ''
                  )}
                  {!item.instead_price && item.filter_price ? (
                    <span css={{ fontSize: 32, lineHeight: '32px' }}>
                      {item.display_price.indexOf('от') !== -1 ? 'от ' : ''}
                      {splitPriceByThree(item.filter_price)}₽
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              )}
            <div
              css={{
                [DEVICE_SCREENS_MAX_STRING.sm]: {
                  display: 'none',
                },
              }}
            >
              {!isEventPassed(item) && item.dates_txt && (
                <RedButton
                  disabled={disabled}
                  montserrat
                  callback={() => {
                    onButtonClick();
                  }}
                  subscription_confirmed={
                    item.status === activeStatus && item.subscription_confirmed
                  }
                  {...(tsNextWidgetConfig && {
                    'data-type': tsNextWidgetConfig.type,
                    'data-uid': tsNextWidgetConfig.id,
                    className: 'tsnext-widget',
                  })}
                >
                  {item.status === activeStatus &&
                    item.subscription_confirmed && (
                      <CheckMarkSVG css={{ width: 16, height: 14 }} />
                    )}
                  {getBuyButtonText(item, isRanOutOfTickets)}
                </RedButton>
              )}
            </div>
            <FavoriteButtonWrapper actionShip>
              <FavoriteButton
                kind={FavoriteKinds.event}
                id={item.remote_id}
                size='medium'
                variant='red-outline'
                actionShip
              />
            </FavoriteButtonWrapper>
          </div>
          <div
            css={{
              display: 'none',
              marginTop: 10,
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                display: 'flex',
                justifyContent: 'space-between',
              },
            }}
          >
            {!!item.views && (
              <div css={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <ViewSVG css={{ width: 20, height: 18 }} /> {item.views}
              </div>
            )}
            {item.age_category && <div>{item.age_category}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
